import React from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { SessionVM } from "./scheduleTypes";
import { EventVM } from "./eventVM";
import { eventTypeLabel } from "../../types/event";
import { ColorMap, resolveTrainingTypeColor } from "../../utils/scheduleColors";
import { getLocalDateString, isSameDay, startOfToday } from "./scheduleDateUtils";
import { ScheduledRecovery } from "../../types/scheduledRecovery";

interface MonthViewProps {
  month: Date;
  selectedDay: Date | null;
  getSessionsForDate: (date: Date) => SessionVM[];
  /** Events on a day → a bar marker under the date (Events.md §4). */
  getEventsForDate?: (date: Date) => EventVM[];
  getRecoveriesForDate?: (date: Date) => ScheduledRecovery[];
  trainingTypeColors?: ColorMap;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  onSelectDay: (date: Date) => void;
}

const WEEKDAYS = ["M", "T", "W", "T", "F", "S", "S"];

const MonthView: React.FC<MonthViewProps> = ({
  month,
  selectedDay,
  getSessionsForDate,
  getEventsForDate,
  getRecoveriesForDate,
  trainingTypeColors,
  onPrevMonth,
  onNextMonth,
  onSelectDay,
}) => {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const firstDay = new Date(year, monthIndex, 1);
  const lastDay = new Date(year, monthIndex + 1, 0);
  const startOffset = (firstDay.getDay() + 6) % 7; // Monday-first

  const cells: (Date | null)[] = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) {
    cells.push(new Date(year, monthIndex, d));
  }

  const today = startOfToday();

  return (
    <div>
      {/* Month navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={onPrevMonth}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-base-300/60 text-base-content hover:bg-base-300 transition-colors"
          aria-label="Previous month"
        >
          <ChevronLeftIcon className="w-5 h-5" />
        </button>
        <h3 className="text-base font-semibold text-base-content font-grotesk">
          {month.toLocaleDateString("en-GB", {
            month: "long",
            year: "numeric",
          })}
        </h3>
        <button
          onClick={onNextMonth}
          className="flex h-9 w-9 items-center justify-center rounded-full bg-base-300/60 text-base-content hover:bg-base-300 transition-colors"
          aria-label="Next month"
        >
          <ChevronRightIcon className="w-5 h-5" />
        </button>
      </div>

      <div className="mt-4 grid grid-cols-7 gap-1">
        {WEEKDAYS.map((day, i) => (
          <div
            key={i}
            className="py-2 text-center text-xs font-medium text-base-content/40 font-grotesk"
          >
            {day}
          </div>
        ))}

        {cells.map((day, index) => {
          if (!day) return <div key={`empty-${index}`} className="h-12" />;

          const sessions = getSessionsForDate(day);
          const events = getEventsForDate?.(day) ?? [];
          const recoveries = getRecoveriesForDate?.(day) ?? [];
          const event = events[0];
          const isToday = isSameDay(day, today);
          const isSelected = selectedDay ? isSameDay(day, selectedDay) : false;

          return (
            <button
              key={getLocalDateString(day)}
              onClick={() => onSelectDay(day)}
              title={event ? `${event.name} · ${eventTypeLabel(event.type)}` : undefined}
              className={`relative flex h-12 flex-col items-center justify-center rounded-xl transition-colors ${
                isSelected
                  ? "bg-primary text-primary-content"
                  : isToday
                    ? "bg-base-300/60"
                    : "hover:bg-base-300/40"
              }`}
            >
              {event && (
                <span
                  aria-hidden
                  className="absolute bottom-1 left-2 right-2 h-0.5 rounded-full"
                  style={{
                    backgroundColor: isSelected ? "currentColor" : event.color,
                  }}
                />
              )}
              <span
                className={`text-sm ${
                  isSelected
                    ? "font-semibold text-primary-content"
                    : isToday
                      ? "font-semibold text-base-content"
                      : "text-base-content/80"
                }`}
              >
                {day.getDate()}
              </span>
              {recoveries.length > 0 && (
                <span
                  aria-hidden
                  className={`absolute top-1 right-1 h-1.5 w-1.5 rounded-full ${
                    isSelected ? "bg-primary-content" : "bg-sky-500"
                  }`}
                />
              )}
              {sessions.length > 0 && (
                <div className="mt-1 flex gap-0.5">
                  {sessions.slice(0, 3).map((s, i) => (
                    <span
                      key={i}
                      className="h-1.5 w-1.5 rounded-full"
                      style={{
                        backgroundColor: isSelected
                          ? "currentColor"
                          : resolveTrainingTypeColor(
                              s.typeColorKey,
                              trainingTypeColors,
                            ),
                      }}
                    />
                  ))}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MonthView;
